import { SITE_NAME, absoluteUrl } from '@/lib/config';
import {
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_SIZE,
  OG_LOCALE,
  formatTitle,
  serializeJsonLd,
  withInLanguage,
} from './utils';

export interface SeoProps {
  /** Page title; the site name is appended (`Shop | Shree Balaji Granites`). Omit on the home page. */
  title?: string;
  description?: string;
  /**
   * Canonical path on this site: `/shop/tan-brown-granite-slab`. Defaults to `/`.
   */
  path?: string;
  /** Social-card image: absolute URL or site-relative path. Omitted ⇒ the default site image. */
  image?: string;
  imageAlt?: string;
  /** Private/utility pages (account, admin, auth): `noindex, nofollow`, no canonical link. */
  noindex?: boolean;
  type?: 'website' | 'article' | 'product';
  /** Structured data, one `<script type="application/ld+json">` per object. */
  jsonLd?: readonly Record<string, unknown>[];
}

/**
 * Per-route document metadata using React 19's native hoisting (D-033): the `<title>`, `<meta>` and
 * `<link>` elements below are rendered anywhere in the tree and React moves them into `<head>` —
 * on the client, and (for pre-rendering) in the `react-dom/static` output. No provider, no effects,
 * no `window` access, so it is SSR-safe.
 *
 * The site is English only (D-358): an indexable page has one canonical link, `og:locale` is
 * `en_IN` and the structured data says `inLanguage: en-IN`.
 *
 * Render exactly ONE `<Seo>` per route: React does not de-duplicate `<meta>` tags.
 */
export function Seo({
  title,
  description,
  path = '/',
  image,
  imageAlt,
  noindex = false,
  type = 'website',
  jsonLd,
}: SeoProps) {
  const fullTitle = formatTitle(title);
  const socialTitle = title?.trim() ? title.trim() : SITE_NAME;
  const url = absoluteUrl(path);
  const hasOwnImage = Boolean(image);
  const imageUrl = absoluteUrl(image ?? DEFAULT_OG_IMAGE);

  return (
    <>
      <title>{fullTitle}</title>
      {description ? <meta name="description" content={description} /> : null}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <>
          <link rel="canonical" href={url} />
        </>
      )}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={socialTitle} />
      {description ? <meta property="og:description" content={description} /> : null}
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={OG_LOCALE} />
      <meta property="og:image" content={imageUrl} />
      {hasOwnImage ? null : (
        <>
          <meta property="og:image:width" content={String(DEFAULT_OG_IMAGE_SIZE.width)} />
          <meta property="og:image:height" content={String(DEFAULT_OG_IMAGE_SIZE.height)} />
        </>
      )}
      {imageAlt || !hasOwnImage ? (
        <meta property="og:image:alt" content={imageAlt ?? SITE_NAME} />
      ) : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={socialTitle} />
      {description ? <meta name="twitter:description" content={description} /> : null}
      <meta name="twitter:image" content={imageUrl} />

      {jsonLd?.map((data, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: serializeJsonLd(withInLanguage(data)) }}
        />
      ))}
    </>
  );
}
